import { Input, InputNumber } from 'antd/lib';
import Button from 'antd/lib/button';
import Form from 'antd/lib/form';
import TextArea from 'antd/lib/input/TextArea';
import Modal from 'antd/lib/modal';
import Select from 'antd/lib/select';
import Typography from 'antd/lib/typography';
import { PjmAgreementType, PjmBatteryChemistryType, PjmBatteryType } from 'src/domain/pjm/dto/enums';
import { PjmBatteryInfo } from 'src/domain/pjm/dto/PjmBatteryInfo';

const agreementTypeOptions = Object.values(PjmAgreementType).map(it => ({ label: it, value: it }));
const chemistryOptions = Object.values(PjmBatteryChemistryType).map(it => ({ label: it, value: it }));
const typeOptions = Object.values(PjmBatteryType).map(it => ({ label: it, value: it }));

interface LocationBatteryModalProps {
    value?: PjmBatteryInfo & { _id: string };
    onClose: (actionTaken: boolean) => void;
    onChange: (data: PjmBatteryInfo & { _id: string }) => void;
}

export function LocationBatteryModal({ value, onClose, onChange }: LocationBatteryModalProps) {
    const [form] = Form.useForm<any>();

    const isNew = !value;

    const currentYear = new Date().getFullYear();
    const vintageOptions = [];
    for (let year = currentYear; year >= 1950; year--) {
        vintageOptions.push({ label: year, value: year });
    }

    function onFormFinish(data: PjmBatteryInfo & { _id: string }) {
        console.info('LocationBatteryModal onFormFinish', data);
        onChange(data);
    }

    return (
        <Modal
            title={isNew ? 'Add Battery' : 'Edit Battery'}
            className="pjm-location-battery-modal"
            open
            destroyOnHidden
            maskClosable={false}
            keyboard={false}
            onCancel={() => onClose(false)}
            width="500px"
            footer={[
                <Button key="pjm-location-battery-modal-cancel" onClick={() => onClose(false)}>
                    Cancel
                </Button>,
                <Button key="pjm-location-battery-modal-submit" type="primary" onClick={form.submit}>
                    Save
                </Button>,
            ]}
        >
            <Form form={form} name="pjm-location-battery-form" preserve={false} layout="vertical" onFinish={onFormFinish} initialValues={value}>
                <Form.Item name={['id']} hidden>
                    <Input disabled />
                </Form.Item>
                <Form.Item name={['_id']} hidden>
                    <Input disabled />
                </Form.Item>
                <Form.Item
                    name={['maxOutput']}
                    label={<Typography.Text strong>Max Output (kW)</Typography.Text>}
                    hasFeedback
                    rules={[{ required: true, message: 'Please enter value!' }]}
                >
                    <InputNumber style={{ width: '100px' }} min={0} max={500000} />
                </Form.Item>
                <Form.Item name={['capacity']} label={<Typography.Text strong>Battery Capacity (kW 1C)</Typography.Text>}>
                    <InputNumber style={{ width: '100px' }} min={0} max={500000} />
                </Form.Item>
                <Form.Item name={['vintage']} label={<Typography.Text strong>Vintage</Typography.Text>}>
                    <Select showSearch autoClearSearchValue optionFilterProp="label" placeholder="Select vintage year" options={vintageOptions} />
                </Form.Item>
                <Form.Item name={['chemistry']} label={<Typography.Text strong>Chemistry</Typography.Text>}>
                    <Select showSearch autoClearSearchValue optionFilterProp="label" placeholder="Select chemistry" options={chemistryOptions} />
                </Form.Item>
                <Form.Item name={['type']} label={<Typography.Text strong>Type</Typography.Text>}>
                    <Select showSearch autoClearSearchValue optionFilterProp="label" placeholder="Select Type" options={typeOptions} />
                </Form.Item>
                <Form.Item
                    name={['agreementType']}
                    label={<Typography.Text strong>Agreement Type</Typography.Text>}
                    hasFeedback
                    rules={[{ required: true, message: 'Please select agreement type!' }]}
                >
                    <Select
                        showSearch
                        autoClearSearchValue
                        optionFilterProp="label"
                        placeholder="Select agreement type"
                        options={agreementTypeOptions}
                    />
                </Form.Item>
                <Form.Item
                    name={['injectionRight']}
                    label={<Typography.Text strong>Injection Rights (MW) (Required for ISA and WMPA)</Typography.Text>}
                    rules={[
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (value == null && [PjmAgreementType.ISA, PjmAgreementType.WMPA].includes(getFieldValue(['agreementType']))) {
                                    return Promise.reject(new Error('Please enter value!'));
                                }
                                return Promise.resolve();
                            },
                        }),
                    ]}
                >
                    <InputNumber style={{ width: '100px' }} min={0} max={9999.999} />
                </Form.Item>
                <Form.Item name={['note']} label={<Typography.Text strong>Note</Typography.Text>}>
                    <TextArea rows={4} maxLength={500} showCount />
                </Form.Item>
            </Form>
        </Modal>
    );
}
